import { CircleAlert, CircleCheck, Info, TriangleAlert } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export type Tone = "neutral" | "info" | "success" | "warning" | "danger";

const TONE: Record<Tone, string> = {
  neutral: "border-line bg-surface-sunken text-fg-secondary",
  info: "border-info-border/40 bg-info-bg text-info-fg",
  success: "border-success-border/40 bg-success-bg text-success-fg",
  warning: "border-warning-border/50 bg-warning-bg text-warning-fg",
  danger: "border-danger-border/40 bg-danger-bg text-danger-fg",
};

const ICON = {
  neutral: Info,
  info: Info,
  success: CircleCheck,
  warning: TriangleAlert,
  danger: CircleAlert,
};

/**
 * One quiet block of state: an icon (so tone is never colour alone) and text. `danger` is an
 * alert announced to assistive technology; the others are plain text unless `live` is set.
 */
export function Notice(props: {
  tone?: Tone;
  children: ReactNode;
  title?: ReactNode;
  action?: ReactNode;
  live?: boolean;
  className?: string;
}) {
  const tone = props.tone ?? "neutral";
  const Icon = ICON[tone];
  return (
    <div
      role={tone === "danger" ? "alert" : props.live ? "status" : undefined}
      className={cn(
        "flex items-start gap-2.5 rounded-md border px-3 py-2.5 text-sm leading-snug",
        TONE[tone],
        props.className,
      )}
    >
      <Icon aria-hidden="true" className="mt-px size-4 shrink-0" strokeWidth={1.75} />
      <div className="flex min-w-0 flex-1 flex-col gap-1">
        {props.title ? <p className="font-medium">{props.title}</p> : null}
        <div className={props.title ? "text-fg-secondary" : undefined}>{props.children}</div>
        {props.action ? <div className="mt-1">{props.action}</div> : null}
      </div>
    </div>
  );
}

/** A one-line inline error under a control or action row. */
export function InlineError({ children, id }: { children: ReactNode; id?: string }) {
  return (
    <p id={id} role="alert" className="flex items-start gap-1.5 text-sm text-danger-fg">
      <CircleAlert aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" strokeWidth={2} />
      <span>{children}</span>
    </p>
  );
}
